/**
 * QuickFieldNotes - Lightweight field notes for Estimates
 * 
 * Simple capture only:
 * - Notes (text)
 * - Photos (basic upload)
 * - Measurements (simple key-value)
 * 
 * NO checklist, NO voice recordings, NO structured discovery
 * That's what Site Visits are for.
 * 
 * Features:
 * - Auto-save every 10 seconds
 * - Save to camera roll option for photos
 */

import { useState, useRef, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import CameraCaptureModal from '@/components/CameraCaptureModal';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { 
  X, 
  Plus, 
  Trash2, 
  Save,
  Camera,
  ImagePlus,
  StickyNote,
  Ruler,
  Check,
  Download
} from 'lucide-react';

// Auto-save interval
const AUTO_SAVE_INTERVAL = 10000;

const QuickFieldNotes = ({ 
  isOpen, 
  onClose, 
  onSave,
  initialData = {},
  estimateId = null,  // For auto-save tracking
  orgId = null
}) => {
  const [notes, setNotes] = useState(initialData.notes || '');
  const [photos, setPhotos] = useState(initialData.photos || []);
  const [measurements, setMeasurements] = useState(initialData.measurements || []);
  const fileInputRef = useRef(null);
  const libraryInputRef = useRef(null);
  const [cameraOpen, setCameraOpen] = useState(false);
  
  // Auto-save state
  const [lastAutoSaved, setLastAutoSaved] = useState(null);
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const autoSaveTimerRef = useRef(null);

  // Gather current data
  const gatherCurrentData = useCallback(() => ({
    notes,
    photos,
    measurements
  }), [notes, photos, measurements]);

  // Auto-save to localStorage (simpler for estimates)
  const performAutoSave = useCallback(async () => {
    if (!estimateId) return;
    
    try {
      setIsAutoSaving(true);
      const data = gatherCurrentData();
      localStorage.setItem(`quick_notes_draft_${estimateId}`, JSON.stringify({
        data,
        savedAt: new Date().toISOString()
      }));
      setLastAutoSaved(new Date());
      setHasUnsavedChanges(false);
      console.log('[QuickFieldNotes] Auto-saved at', new Date().toLocaleTimeString());
    } catch (error) {
      console.error('[QuickFieldNotes] Auto-save failed:', error);
    } finally {
      setIsAutoSaving(false);
    }
  }, [estimateId, gatherCurrentData]);

  // Track changes
  useEffect(() => {
    if (!isOpen) return;
    setHasUnsavedChanges(true);
  }, [notes, photos, measurements, isOpen]);

  // Auto-save timer
  useEffect(() => {
    if (!isOpen || !estimateId) return;

    if (autoSaveTimerRef.current) {
      clearInterval(autoSaveTimerRef.current);
    }

    autoSaveTimerRef.current = setInterval(() => {
      if (hasUnsavedChanges) {
        performAutoSave();
      }
    }, AUTO_SAVE_INTERVAL);

    // Initial save after 3 seconds
    const initialTimer = setTimeout(() => {
      if (hasUnsavedChanges) performAutoSave();
    }, 3000);

    return () => {
      clearInterval(autoSaveTimerRef.current);
      clearTimeout(initialTimer);
    };
  }, [isOpen, estimateId, hasUnsavedChanges, performAutoSave]);

  // Load draft on mount
  useEffect(() => {
    if (!isOpen || !estimateId) return;
    
    const draft = localStorage.getItem(`quick_notes_draft_${estimateId}`);
    if (draft) {
      try {
        const { data, savedAt } = JSON.parse(draft);
        // Only load if draft is newer than initial data
        if (data && (!initialData.notes || data.notes !== initialData.notes)) {
          setNotes(data.notes || '');
          setPhotos(data.photos || []);
          setMeasurements(data.measurements || []);
          toast.info('Restored from auto-saved draft');
        }
      } catch (e) {
        console.error('Failed to load draft:', e);
      }
    }
  }, [isOpen, estimateId]);

  // Save photo to camera roll / download
  const savePhotoToDevice = async (photo) => {
    try {
      const link = document.createElement('a');
      link.href = photo.dataUrl;
      link.download = `field_photo_${Date.now()}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast.success('Photo saved to device');
    } catch (error) {
      toast.error('Failed to save photo');
    }
  };

  const handlePhotoUpload = (e) => {
    const files = Array.from(e.target.files);
    
    files.forEach(file => {
      if (file.size > 10 * 1024 * 1024) {
        toast.error('Photo too large (max 10MB)');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          dataUrl: event.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
    
    e.target.value = '';
  };

  const deletePhoto = (id) => {
    setPhotos(prev => prev.filter(p => p.id !== id));
  };

  const addMeasurement = () => {
    setMeasurements(prev => [...prev, {
      id: Date.now(),
      label: '',
      value: ''
    }]);
  };

  const updateMeasurement = (id, field, value) => {
    setMeasurements(prev => prev.map(m => 
      m.id === id ? { ...m, [field]: value } : m
    ));
  };

  const deleteMeasurement = (id) => {
    setMeasurements(prev => prev.filter(m => m.id !== id));
  };

  const handleSave = () => {
    onSave({
      notes,
      photos,
      measurements
    });
    toast.success('Field notes saved');
    onClose();
  };

  if (!isOpen) return null;

  // Use Portal to render at document body level, avoiding stacking context issues
  return createPortal(
    <>
      {/* Backdrop - z-[9998] to be above everything */}
      <div 
        className="fixed inset-0 bg-black/50 z-[9998]"
        onClick={onClose}
      />
      
      {/* Drawer - z-[9999] to be above everything including mobile headers/banners */}
      <div 
        className="fixed inset-y-0 right-0 w-full max-w-md bg-white shadow-2xl z-[9999] flex flex-col"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        {/* Header with auto-save status and safe area padding */}
        <div className="flex items-center justify-between p-4 pt-[max(1rem,env(safe-area-inset-top))] border-b border-gray-200 bg-primary text-white">
          <div className="flex items-center gap-3">
            <StickyNote className="w-5 h-5" />
            <div>
              <h2 className="font-heading text-lg font-bold">Quick Field Notes</h2>
              {isAutoSaving ? (
                <p className="text-xs opacity-80 flex items-center gap-1">
                  <span className="w-2 h-2 bg-yellow-400 rounded-full animate-pulse" />
                  Saving...
                </p>
              ) : lastAutoSaved ? (
                <p className="text-xs opacity-80 flex items-center gap-1">
                  <Check className="w-3 h-3" />
                  Auto-saved {lastAutoSaved.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </p>
              ) : (
                <p className="text-xs opacity-80">Optional - for quick capture</p>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              onClick={handleSave}
              size="sm"
              className="bg-green-600 hover:bg-green-700 text-white h-9 px-3"
            >
              <Save className="w-4 h-4 mr-1" />
              Save
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="text-white hover:bg-white/20"
            >
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>
        
        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4 space-y-6">
          
          {/* Notes */}
          <div className="space-y-2">
            <Label className="text-base font-semibold">Notes</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Quick notes about this estimate..."
              className="min-h-[120px]"
            />
          </div>

          {/* Photos */}
          <div className="space-y-3">
            <Label className="text-base font-semibold">Photos</Label>
            
            <div className="grid grid-cols-2 gap-2">
              <Button
                variant="outline"
                onClick={() => setCameraOpen(true)}
                className="h-12"
                data-testid="quick-take-photo-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                Take Photo
              </Button>
              <Button
                variant="outline"
                onClick={() => libraryInputRef.current?.click()}
                className="h-12"
                data-testid="quick-upload-photo-btn"
              >
                <ImagePlus className="w-4 h-4 mr-2" />
                From Library
              </Button>
            </div>
            <input
              ref={libraryInputRef}
              type="file"
              accept="image/*"
              multiple
              onChange={handlePhotoUpload}
              className="hidden"
            />
            <CameraCaptureModal
              open={cameraOpen}
              onClose={() => setCameraOpen(false)}
              onCapture={handlePhotoUpload}
            />
            
            {photos.length > 0 ? (
              <div className="grid grid-cols-3 gap-2">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative group aspect-square">
                    <img 
                      src={photo.dataUrl} 
                      alt={photo.name}
                      className="w-full h-full object-cover rounded-lg border border-gray-200"
                    />
                    {/* Photo action buttons */}
                    <div className="absolute top-1 right-1 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <Button
                        variant="secondary"
                        size="icon"
                        className="h-6 w-6 bg-white/90"
                        onClick={() => savePhotoToDevice(photo)}
                        title="Save to device"
                      >
                        <Download className="w-3 h-3" />
                      </Button>
                      <Button
                        variant="destructive"
                        size="icon"
                        className="h-6 w-6"
                        onClick={() => deletePhoto(photo.id)}
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 border-2 border-dashed border-gray-200 rounded-lg">
                <ImagePlus className="w-8 h-8 text-muted-foreground mx-auto mb-1 opacity-40" />
                <p className="text-sm text-muted-foreground">No photos</p>
              </div>
            )}
          </div>

          {/* Measurements */}
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <Label className="text-base font-semibold">Measurements</Label>
              <span className="text-xs text-muted-foreground">{measurements.length} added</span>
            </div>
            
            {measurements.map((measurement) => (
              <div key={measurement.id} className="flex items-center gap-2">
                <Input
                  value={measurement.label}
                  onChange={(e) => updateMeasurement(measurement.id, 'label', e.target.value)}
                  placeholder="Label (e.g., Length)"
                  className="flex-1"
                />
                <Input
                  value={measurement.value}
                  onChange={(e) => updateMeasurement(measurement.id, 'value', e.target.value)}
                  placeholder="Value (e.g., 20 ft)"
                  className="w-32"
                />
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => deleteMeasurement(measurement.id)}
                  className="text-muted-foreground hover:text-destructive flex-shrink-0"
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              </div>
            ))}
            
            <Button
              variant="outline"
              onClick={addMeasurement}
              className="w-full"
              size="sm"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Measurement
            </Button>
          </div>
        </div>
        
        {/* Footer - Save All Photos button */}
        {photos.length > 0 && (
          <div className="p-4 border-t border-gray-200 bg-gray-50">
            <Button
              variant="outline"
              onClick={() => photos.forEach(p => savePhotoToDevice(p))}
              className="w-full h-10"
            >
              <Download className="w-4 h-4 mr-2" />
              Save All Photos to Device ({photos.length})
            </Button>
          </div>
        )}
      </div>
    </>,
    document.body
  );
};

export default QuickFieldNotes;
